/**
 * Safe Energy Display Guard
 * 
 * DISPLAY-ONLY safety guard for live charging UI.
 * Prevents UI from showing incorrect cumulative charger values during:
 * - Session start
 * - SSE reconnects
 * - Transient state issues
 * 
 * IMPORTANT:
 * - Backend remains source of truth for billing and final totals
 * - Do NOT apply to summary/final session values
 * - Do NOT change payment capture amounts
 * 
 * This is purely for UX stability on the live charging screen.
 */

/**
 * Get safe energy value for live display
 * Guards against:
 * 1. Invalid values (NaN, negative, null)
 * 2. Suspiciously large values (>100 kWh) that indicate cumulative meter leakage
 * 
 * @param {Object} params
 * @param {number|string} params.energyKwh - Current energy value to validate
 * @param {number} params.meterStartKwh - Meter reading when session started (optional)
 * @param {number} params.latestMeterKwh - Latest cumulative meter reading (optional)
 * @returns {number} Safe energy value for display (always >= 0)
 */
export function getSafeEnergyDisplay({
  energyKwh,
  meterStartKwh,
  latestMeterKwh
}) {
  const parsed = Number(energyKwh);

  // Guard 1: Invalid / NaN / negative
  if (!Number.isFinite(parsed) || parsed < 0) {
    // Try fallback calculation if meter values available
    if (meterStartKwh != null && latestMeterKwh != null) {
      const fallback = Number(latestMeterKwh) - Number(meterStartKwh);
      console.warn('[EnergyDisplayGuard] Invalid energy value detected, using fallback', {
        energyKwh,
        meterStartKwh,
        latestMeterKwh,
        fallback: fallback.toFixed(3)
      });
      return Math.max(0, Number.isFinite(fallback) ? fallback : 0);
    }

    console.warn('[EnergyDisplayGuard] Invalid energy value detected, clamping to 0', {
      energyKwh,
      meterStartKwh,
      latestMeterKwh
    });
    return 0;
  }

  // Guard 2: Suspiciously large value (>100 kWh)
  // This likely indicates a cumulative/lifetime meter value leaked into live display
  // Most sessions deliver 5-30 kWh, so 100 kWh is a conservative safety threshold
  if (parsed > 100) {
    // Try fallback calculation if meter values available
    if (meterStartKwh != null && latestMeterKwh != null) {
      const fallback = Number(latestMeterKwh) - Number(meterStartKwh);
      console.warn('[EnergyDisplayGuard] Suspiciously large live energy value detected, using fallback', {
        energyKwh: parsed.toFixed(3),
        meterStartKwh,
        latestMeterKwh,
        fallback: fallback.toFixed(3),
        timestamp: new Date().toISOString()
      });
      return Math.max(0, Number.isFinite(fallback) ? fallback : 0);
    }

    // No meter values for fallback - clamp to 0 to prevent showing wrong value
    console.warn('[EnergyDisplayGuard] Suspiciously large live energy value detected, clamping to 0', {
      energyKwh: parsed.toFixed(3),
      meterStartKwh,
      latestMeterKwh,
      timestamp: new Date().toISOString()
    });
    return 0;
  }

  // Value passes all guards - return as-is
  return parsed;
}
